import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

const TeamUpCalendarManager = ({ userData }) => {
  const [events, setEvents] = useState([]);
  const [subcalendars, setSubcalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    subcalendar_ids: [],
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    title: '',
    notes: '',
    location: '',
  });

  // Fetch events and subcalendars on component mount
  useEffect(() => {
    fetchEvents();
    fetchSubcalendars();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/field-experience-events/teamup/events', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data.events || []);
    } catch (err) {
      setError('Failed to fetch events: ' + (err.response?.data?.error || err.message));
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcalendars = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/field-experience-events/teamup/subcalendars', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubcalendars(res.data.subcalendars || []);
    } catch (err) {
      console.error('Error fetching subcalendars:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubcalendarChange = (subcalendarId, checked) => {
    setFormData(prev => {
      const ids = prev.subcalendar_ids || [];
      if (checked) {
        return { ...prev, subcalendar_ids: [...ids, subcalendarId] };
      } else {
        return { ...prev, subcalendar_ids: ids.filter(id => id !== subcalendarId) };
      }
    });
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return '';
    let dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    let t = time.length === 5 ? time + ':00' : time;
    return `${dateStr}T${t}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (formData.subcalendar_ids.length === 0) {
      setError('Please select at least one subcalendar');
      return;
    }
    if (!formData.start_date || !formData.start_time || !formData.end_date || !formData.end_time) {
      setError('Please fill in all date and time fields');
      return;
    }
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const start_dt = formatDateTime(formData.start_date, formData.start_time);
      const end_dt = formatDateTime(formData.end_date, formData.end_time);
      
      const payload = {
        subcalendar_ids: formData.subcalendar_ids.map(id => parseInt(id)),
        start_dt,
        end_dt,
        title: formData.title,
        notes: formData.notes || '',
        location: formData.location || '',
      };

      await axios.post('/api/field-experience-events/teamup/events', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Event created successfully!');
      setFormData({
        subcalendar_ids: [],
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        title: '',
        notes: '',
        location: '',
      });
      setShowCreateForm(false);
      fetchEvents(); // Refresh events list
    } catch (err) {
      setError('Failed to create event: ' + (err.response?.data?.error || err.message));
      console.error('Error creating event:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
    },
    buttonSecondary: {
      padding: '10px 20px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
    },
    form: {
      backgroundColor: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
    },
    formGroup: {
      marginBottom: '15px',
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold',
    },
    input: {
      width: '100%',
      padding: '8px',
      fontSize: '14px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      boxSizing: 'border-box',
    },
    checkboxGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '15px',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    textarea: {
      width: '100%',
      padding: '8px',
      fontSize: '14px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      minHeight: '100px',
      fontFamily: 'inherit',
    },
    error: {
      color: '#dc3545',
      marginTop: '10px',
      padding: '10px',
      backgroundColor: '#f8d7da',
      borderRadius: '4px',
    },
    success: {
      color: '#155724',
      marginTop: '10px',
      padding: '10px',
      backgroundColor: '#d4edda',
      borderRadius: '4px',
    },
    eventsList: {
      display: 'grid',
      gap: '15px',
    },
    eventCard: {
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    eventTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '10px',
    },
    eventDetail: {
      marginBottom: '5px',
      color: '#666',
    },
    dateTimeRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>TeamUp Calendar Manager</h1>
        <button
          style={styles.button}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Event'}
        </button>
      </div>

      {showCreateForm && (
        <form style={styles.form} onSubmit={handleSubmit}>
          <h2>Create New Event</h2>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Subcalendars *</label>
            <div style={styles.checkboxGroup}>
              {subcalendars.map(subcal => (
                <label key={subcal.id} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.subcalendar_ids.includes(subcal.id.toString())}
                    onChange={(e) => handleSubcalendarChange(subcal.id.toString(), e.target.checked)}
                  />
                  {subcal.name}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.dateTimeRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Time *</label>
              <input
                type="time"
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
          </div>

          <div style={styles.dateTimeRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Date *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Time *</label>
              <input
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              style={styles.textarea}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <button
            type="submit"
            style={styles.button}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </form>
      )}

      <div>
        <div style={styles.header}>
          <h2>Events</h2>
          <button
            style={styles.buttonSecondary}
            onClick={fetchEvents}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {loading && <LoadingSpinner />}
        
        {error && !showCreateForm && <div style={styles.error}>{error}</div>}
        {success && !showCreateForm && <div style={styles.success}>{success}</div>}

        {!loading && events.length === 0 && (
          <p>No events found. Create your first event above!</p>
        )}

        {!loading && events.length > 0 && (
          <div style={styles.eventsList}>
            {events.map((event) => (
              <div key={event.id} style={styles.eventCard}>
                <div style={styles.eventTitle}>{event.title || 'Untitled Event'}</div>
                <div style={styles.eventDetail}>
                  <strong>Start:</strong> {formatEventDate(event.start_dt)}
                </div>
                <div style={styles.eventDetail}>
                  <strong>End:</strong> {formatEventDate(event.end_dt)}
                </div>
                {event.location && (
                  <div style={styles.eventDetail}>
                    <strong>Location:</strong> {event.location}
                  </div>
                )}
                {event.notes && (
                  <div style={styles.eventDetail}>
                    <strong>Notes:</strong> {event.notes}
                  </div>
                )}
                <div style={styles.eventDetail}>
                  <strong>Event ID:</strong> {event.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamUpCalendarManager;
